import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { useToast } from '@/hooks/use-toast';
import { userService } from '@/services/user';
import type { CreateUserResponse } from '@/types/user';
import {
  getAssignableCollaboratorRoles,
  isBrandScopedUser,
  resolveBrandOptions,
  toApiUserRole,
} from '@/utils/rbac';
import {
  CLIENT_TYPE_HINT,
  COLLABORATOR_TYPE_HINT,
  CREATE_USER_ERROR_TOAST,
  formatWhatsappPayload,
  newUserSchema,
  type NewUserFormValues,
} from '../utils/schema';

type UseNewUserFormParams = {
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
};

export const useNewUserForm = ({ isOpen, onSuccess, onClose }: UseNewUserFormParams) => {
  const { user: currentUser } = useAuth();
  const { partners: catalogPartners } = useData();
  const { toast } = useToast();
  const assignableRoles = getAssignableCollaboratorRoles(currentUser);
  const canCreateCollaborator = assignableRoles.length > 0;
  const canSelectAllBrands = !isBrandScopedUser(currentUser);
  const partners = useMemo(
    () => resolveBrandOptions(currentUser, catalogPartners),
    [catalogPartners, currentUser]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const form = useForm<NewUserFormValues>({
    resolver: zodResolver(newUserSchema),
    defaultValues: {
      accountType: canCreateCollaborator ? 'COLLABORATOR' : 'CLIENT',
      name: '',
      email: '',
      whatsapp: '',
      role: '',
      partnerIds: [],
      allPartners: false,
    },
    mode: 'onSubmit',
  });

  const accountType = form.watch('accountType');
  const partnerIds = form.watch('partnerIds');
  const allPartners = form.watch('allPartners');
  const selectedRole = form.watch('role');

  useEffect(() => {
    if (!isOpen) return;
    form.reset({
      accountType: canCreateCollaborator ? 'COLLABORATOR' : 'CLIENT',
      name: '',
      email: '',
      whatsapp: '',
      role: '',
      partnerIds: [],
      allPartners: false,
    });
    setInviteUrl(null);
    setIsSubmitting(false);
  }, [isOpen, canCreateCollaborator, form]);

  const typeHint = accountType === 'CLIENT' ? CLIENT_TYPE_HINT : COLLABORATOR_TYPE_HINT;

  const togglePartner = (partnerId: string) => {
    if (accountType === 'CLIENT') {
      form.setValue('partnerIds', [partnerId], { shouldValidate: true });
      form.setValue('allPartners', false);
      return;
    }

    const selected = partnerIds.includes(partnerId)
      ? partnerIds.filter((id) => id !== partnerId)
      : [...partnerIds, partnerId];
    form.setValue('partnerIds', selected, { shouldValidate: true });
    form.setValue(
      'allPartners',
      canSelectAllBrands && selected.length === partners.length && partners.length > 0
    );
  };

  const toggleAllPartners = () => {
    if (!canSelectAllBrands) return;
    if (allPartners || partnerIds.length === partners.length) {
      form.setValue('allPartners', false);
      form.setValue('partnerIds', [], { shouldValidate: true });
      return;
    }
    form.setValue(
      'partnerIds',
      partners.map((partner) => partner.id),
      { shouldValidate: true }
    );
    form.setValue('allPartners', true);
  };

  const submit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      const payload = {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        type: values.accountType,
        whatsapp: formatWhatsappPayload(values.whatsapp),
        role: values.accountType === 'COLLABORATOR' ? toApiUserRole(values.role) : undefined,
        partner_ids: values.allPartners ? undefined : values.partnerIds,
        all_partners: values.accountType === 'COLLABORATOR' ? values.allPartners : false,
        partner_id: values.accountType === 'CLIENT' ? values.partnerIds[0] : undefined,
      };

      let response: CreateUserResponse = {};
      try {
        response = await userService.createUser(payload);
      } catch (error) {
        if (values.accountType !== 'CLIENT' || !payload.partner_id) {
          throw error;
        }
        await userService.createPartnerViewer({
          name: payload.name,
          email: payload.email,
          whatsapp: payload.whatsapp,
          partner_id: payload.partner_id,
        });
      }

      if (values.accountType === 'CLIENT') {
        const url = response?.invite_url;
        if (url) {
          setInviteUrl(url);
          onSuccess();
          return;
        }
        toast({
          title: 'Usuário cadastrado com sucesso!',
          description: 'O link de convite fica disponível no menu de ações da lista de usuários.',
        });
        onSuccess();
        onClose();
        return;
      }

      toast({
        title: 'Usuário cadastrado com sucesso',
        description: `O convite foi enviado para ${payload.email}`,
      });
      onSuccess();
      onClose();
    } catch {
      toast({
        variant: 'error',
        title: CREATE_USER_ERROR_TOAST.title,
        description: CREATE_USER_ERROR_TOAST.description,
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  return {
    form,
    accountType,
    partnerIds,
    allPartners,
    selectedRole,
    partners,
    assignableRoles,
    canCreateCollaborator,
    canSelectAllBrands,
    typeHint,
    isSubmitting,
    inviteUrl,
    togglePartner,
    toggleAllPartners,
    submit,
  };
};
